import type { NodeViewProps } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useEditorI18n } from "../../lib/i18n";
import { consumePendingImageUploadBatch } from "../../lib/image-upload-batches";
import {
  isLikelyImageUrl,
  replaceImageUploadNodeWithImages,
} from "../../lib/image-upload-node";
import {
  createUploadRows,
  type UploadImageHandler,
  type UploadRow,
  uploadImageRows,
  validateImageFiles,
} from "../../lib/image-upload-service";

type UseImageUploadRowsOptions = {
  editor: NodeViewProps["editor"];
  getPos: NodeViewProps["getPos"];
  node: NodeViewProps["node"];
  upload?: UploadImageHandler;
  limit: number;
  maxSize: number;
};

type ImageUploadErrorSource = "file" | "url";

export function useImageUploadRows({
  editor,
  getPos,
  node,
  upload,
  limit,
  maxSize,
}: UseImageUploadRowsOptions) {
  const t = useEditorI18n();
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorSource, setErrorSource] = useState<ImageUploadErrorSource | null>(null);
  const [remoteUrl, setRemoteUrl] = useState("");
  const [isSubmittingUrl, setIsSubmittingUrl] = useState(false);

  const isUploading = useMemo(() => rows.some((row) => row.status === "uploading"), [rows]);
  const uploadBatchId = typeof node.attrs.uploadBatchId === "string" ? node.attrs.uploadBatchId : null;

  const updateRow = useCallback((id: string, updater: (row: UploadRow) => UploadRow) => {
    setRows((currentRows) => currentRows.map((row) => (row.id === id ? updater(row) : row)));
  }, []);

  const startUploads = useCallback(
    async (files: File[]) => {
      if (!upload) {
        setErrorMessage(t("image.uploadNotConfigured"));
        setErrorSource("file");
        return;
      }

      const uploadRows = createUploadRows(files);
      setErrorMessage(null);
      setErrorSource(null);
      setRows(uploadRows);

      const results = await uploadImageRows({
        rows: uploadRows,
        upload,
        controllers: controllersRef.current,
        onProgress: (rowId, progress) => {
          updateRow(rowId, (currentRow) => ({
            ...currentRow,
            progress,
          }));
        },
        onError: (rowId, nextErrorMessage) => {
          updateRow(rowId, (currentRow) => ({
            ...currentRow,
            status: "error",
            errorMessage: nextErrorMessage,
          }));
        },
      });

      const successfulImages = results
        .filter((result): result is Extract<(typeof results)[number], { status: "success" }> => result.status === "success")
        .map((result) => ({ url: result.url }));

      if (successfulImages.length) {
        replaceImageUploadNodeWithImages({ editor, node, getPos, images: successfulImages });
        return;
      }

      const hasError = results.some((result) => result.status === "error");

      if (!hasError) {
        setRows([]);
      }
    },
    [editor, getPos, node, t, updateRow, upload],
  );

  const handleFiles = useCallback(
    async (incomingFiles: File[]) => {
      const { errorMessage: nextErrorMessage, files } = validateImageFiles({
        incomingFiles,
        limit,
        maxSize,
      });

      if (nextErrorMessage) {
        setErrorMessage(
          nextErrorMessage.includes("Only PNG") ? t("image.supportedTypes") : t("image.helper"),
        );
        setErrorSource("file");
        return;
      }

      await startUploads(files);
    },
    [limit, maxSize, startUploads, t],
  );

  const cancelUpload = useCallback((rowId: string) => {
    controllersRef.current.get(rowId)?.abort();
    controllersRef.current.delete(rowId);
    setRows((currentRows) => currentRows.filter((row) => row.id !== rowId));
  }, []);

  const submitRemoteUrl = useCallback(async () => {
    const trimmedUrl = remoteUrl.trim();

    if (!isLikelyImageUrl(trimmedUrl)) {
      setErrorMessage(t("image.validUrlRequired"));
      setErrorSource("url");
      return;
    }

    setErrorMessage(null);
    setErrorSource(null);
    setIsSubmittingUrl(true);

    const image = new Image();

    const finalize = () => {
      setIsSubmittingUrl(false);
    };

    await new Promise<void>((resolve) => {
      image.onload = () => {
        replaceImageUploadNodeWithImages({ editor, node, getPos, images: [{ url: trimmedUrl }] });
        setRemoteUrl("");
        finalize();
        resolve();
      };

      image.onerror = () => {
        setErrorMessage(t("image.couldNotLoadUrl"));
        setErrorSource("url");
        finalize();
        resolve();
      };

      image.src = trimmedUrl;
    });
  }, [editor, getPos, node, remoteUrl, t]);

  useEffect(() => {
    if (!uploadBatchId || rows.length) {
      return;
    }

    const files = consumePendingImageUploadBatch(uploadBatchId);

    if (!files?.length) {
      return;
    }

    queueMicrotask(() => {
      void handleFiles(files);
    });
  }, [handleFiles, rows.length, uploadBatchId]);

  const helperText = isUploading ? t("image.uploading") : (errorSource === "file" ? errorMessage : t("image.helper"));
  const urlErrorMessage = errorSource === "url" ? errorMessage : null;

  return {
    cancelUpload,
    errorMessage,
    errorSource,
    handleFiles,
    helperText,
    isSubmittingUrl,
    isUploading,
    remoteUrl,
    rows,
    setRemoteUrl,
    submitRemoteUrl,
    urlErrorMessage,
  };
}
